import React from 'react';
import { CommonOnError } from 'types/props';
import classnames from 'classnames';
import { ff } from 'helpers/featureFlags';
import { AttributeAnchors } from 'components/Anchor/Anchor';
import DebugRender from 'components/Debug/Render';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';
import { stateFormErrorConnector, StateFormErrorConnectorProps } from '../shared/stateFormErrors';
import ValidationElement from '../ValidationElement';

interface Props extends ValidationElementProps, StateFormErrorConnectorProps {
  selectedValues?: any
  selectedValueFunc?: any
  name?: string
  onError?: CommonOnError
  className?: string
  attribute?: string
  children?: React.ReactElement[]
  required?: boolean
}

interface State {
  uid: string
  error: boolean
}

class CheckboxGroup extends ValidationElement<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      uid: `${props.name}-${super.guid()}`,
      error: false,
    };
  }

  componentDidMount() {
    this.onUpdate({ value: '' });
  }

  handleValue = (option) => {
    const value = option.value;
    if (!value) {
      return this.props.selectedValues;
    }

    const selected = [...(this.props.selectedValues || [])];
    if (selected.includes(value)) {
      selected.splice(selected.indexOf(value), 1);
    } else {
      selected.push(value);
    }
    return selected;
  };

  onUpdate = (option) => {
    const selectedValues = this.handleValue(option);
    const errors = this.constructor.errors.map((err) => ({
      code: err.code,
      valid: err.func(selectedValues, this.props),
      uid: this.state.uid,
    }));

    this.setState({
      error: errors.some((x) => x.valid === false),
    });

    this.props.onError(selectedValues, errors);
  };

  /**
   * This allows us to switch logic of "hasError" between state.application and
   * state.form. When useStateFormErrors is true, we'll use
   * this.props.hasStateFormError. Otherwise, continue to use the previous error
   * handling.
   */
  hasError = () => {
    if (ff(this.props.features).getActive('EN-4733')) {
      return this.props.hasStateFormError;
    }
    return this.state.error;
  };

  render() {
    const children = React.Children.map(this.props.children, (checkbox) => {
      let checked;

      // Handle empty array case so that .find() doesn't error
      if (!this.props.selectedValues || this.props.selectedValues.length === 0) {
        checked = false;
      } else if (this.props.selectedValues.find) {
        // Check if current value matches one of the checkbox options. Boolify it
        // if a value is found
        checked = !!this.props.selectedValues.find((v) => v === checkbox.props.value);
      }

      // Use function when you want custom behavior
      if (this.props.selectedValueFunc) {
        checked = this.props.selectedValueFunc(checkbox.props);
      }

      const onUpdate = (option) => {
        if (checkbox.props.onUpdate) {
          checkbox.props.onUpdate(option);
        }
        this.onUpdate(option);
      };

      return <checkbox.type {...checkbox.props} checked={checked} onUpdate={onUpdate} disabled={this.props.disabled} />;
    });

    return (
      <div
        className={classnames('blocks', this.props.className, {
          'usa-input-error': this.hasError(),
        })}
      >
        <AttributeAnchors attribute={this.props.attribute} />
        <DebugRender parent={this} />

        {children}
      </div>
    );
  }
}

CheckboxGroup.defaultProps = {
  selectedValues: [],
  disabled: false,
  onError: (value, arr) => arr,
};

CheckboxGroup.errors = [
  {
    code: 'required',
    func: (values, props) => {
      if (props.required) {
        return !!values && !!values.length;
      }
      return true;
    },
  },
];

export default stateFormErrorConnector(CheckboxGroup);
